import { useState, type FormEvent } from "react";
import { useDataProvider, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { CrmDataProvider } from "../providers/types";
import type { LeadAssignmentChannel } from "../types";
import { validateNewChannel } from "./leadAssignmentConfigRules";

/** The channel that is switched on once the Meta integration is connected (a catalogue row). */
const META_CHANNEL_CODE = "meta";

type Props = {
  channels: LeadAssignmentChannel[];
  saving: boolean;
  save: (write: () => Promise<unknown>) => Promise<void>;
};

/**
 * Channel catalogue card of the "Asignación de leads" screen: activate/deactivate a channel
 * and add a new one (label + code). Channel codes are plain data; nothing restricts them.
 */
export const LeadAssignmentChannels = ({ channels, saving, save }: Props) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const result = validateNewChannel(
      label,
      code,
      channels.map((c) => c.code),
    );
    setError(result.error);
    if (result.error) return;
    await save(() =>
      dataProvider.saveLeadAssignmentChannel({
        code: result.code,
        label: result.label,
        is_active: true,
      }),
    );
    setLabel("");
    setCode("");
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h2 className="font-medium">
          {translate("crm.leadAssignment.channels_title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.leadAssignment.channels_help")}
        </p>
        <ul className="divide-y">
          {channels.map((channel) => (
            <li
              key={channel.code}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="font-medium">{channel.label}</p>
                <p className="text-xs text-muted-foreground">
                  {channel.code}
                  {" · "}
                  {translate(
                    channel.is_active
                      ? "crm.leadAssignment.channel_active"
                      : "crm.leadAssignment.channel_inactive",
                  )}
                  {channel.code === META_CHANNEL_CODE && !channel.is_active
                    ? ` · ${translate("crm.leadAssignment.meta_note")}`
                    : ""}
                </p>
              </div>
              <Switch
                aria-label={translate("crm.leadAssignment.channel_toggle", {
                  label: channel.label,
                })}
                checked={channel.is_active}
                disabled={saving}
                onCheckedChange={(is_active) =>
                  save(() =>
                    dataProvider.saveLeadAssignmentChannel({
                      code: channel.code,
                      label: channel.label,
                      is_active,
                    }),
                  )
                }
              />
            </li>
          ))}
        </ul>

        <form
          onSubmit={onSubmit}
          className="space-y-2 border-t pt-3"
          noValidate
        >
          <h3 className="text-sm font-medium">
            {translate("crm.leadAssignment.add_channel_title")}
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="lead-assignment-channel-label">
                {translate("crm.leadAssignment.channel_label")}
              </Label>
              <Input
                id="lead-assignment-channel-label"
                className="h-11"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lead-assignment-channel-code">
                {translate("crm.leadAssignment.channel_code")}
              </Label>
              <Input
                id="lead-assignment-channel-code"
                className="h-11"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {translate(`crm.leadAssignment.channel_error_${error}`)}
            </p>
          ) : null}
          <Button type="submit" className="min-h-11" disabled={saving}>
            {translate("crm.leadAssignment.add_channel")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
